import { useMemo } from 'react';
import { useGetProductAddOns } from 'typescript/hooks/use_product_client';
import { getLocaleStringInternal } from 'typescript/utilities/locales';
import { getAvt } from 'utilities/cookies';
import { trackCdp } from 'utilities/tracking';

interface UseAddOnsProps {
  product: Record<string, any>;
  currentProduct: Record<string, any>;
  cart: Record<string, any>;
  experiments: Record<string, any>;
}

/**
 * Hook for getting product add-ons
 * @param product - The product to get the add-ons for
 * @param currentProduct - The current product
 * @param cart - The cart
 * @param experiments - The experiments
 * @returns addOns - The add-ons
 * @returns isGetAddOnsLoading - Whether the add-ons are loading
 * @returns isGetAddOnsError - Whether the add-ons are error
 */
export const useAddOns = ({
  product,
  currentProduct,
  cart,
  experiments,
}: UseAddOnsProps) => {
  const {
    isLoading: isGetAddOnsLoading,
    isError: isGetAddOnsError,
    data: getAddOnsData,
  } = useGetProductAddOns({
    productSlug: currentProduct.slug,
    locale: getLocaleStringInternal(),
    enabled:
      currentProduct.add_ons_category_count > 0 && !currentProduct?.add_ons,
    onLoadTimeTracked: (loadTimeMs) => {
      const experiment = experiments?.pdpPerformanceImprovementsExperiment;

      // Report to CDP for experiment tracking
      if (experiment?.is_treatment) {
        const entityType = 'anonymous_visitor';
        const entityId = getAvt();
        const eventName = 'PDP Page Load Time - Frontend';
        const properties = {
          order_number: cart?.number,
          product_key: product?.product_key,
          variation: experiment.variation,
          experiment_name: experiment.bucketing_flag,
          track_id: experiment.track_id,
          load_time_ms: loadTimeMs,
        };

        trackCdp({
          name: eventName,
          params: {
            entity_type: entityType,
            entity_id: entityId,
            properties,
          },
        });
      }
    },
  });

  // Derive addOns, use query data if available, otherwise use props
  const addOns = useMemo(() => {
    return getAddOnsData?.add_ons ?? currentProduct?.add_ons ?? {};
  }, [getAddOnsData?.add_ons, currentProduct?.add_ons]);

  return {
    addOns,
    isGetAddOnsLoading,
    isGetAddOnsError,
  };
};
